import { BellRing, ChevronRight, Clock, Sparkles } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { MetaLine } from "@/components/ui/meta-line";
import { Skeleton } from "@/components/ui/skeleton";
import { Surface } from "@/components/surface/Surface";
import { cn } from "@/lib/utils";

import { useFirmVisaCounts } from "../hooks/useFirmVisaCounts";
import { useLibrarySearch } from "../hooks/useResearch";
import { RESEARCH_COLUMN } from "../layout";
import { clearRecentSearches, readRecentSearches } from "../recentSearches";
import { suggestionsFor } from "../suggestions";
import type { LibraryHit } from "../types";

const SearchRow: FC<{ query: string; onSearch: (query: string) => void }> = ({
  query,
  onSearch
}) => (
  <button
    type="button"
    onClick={() => onSearch(query)}
    className="-mx-1.5 truncate rounded-md px-1.5 py-1 text-left text-base text-fg-2 hover:bg-fill-1 hover:text-fg"
  >
    {query}
  </button>
);

const Section: FC<{
  title: string;
  icon: FC<{ className?: string }>;
  children: ReactNode;
  action?: ReactNode;
  testId: string;
}> = ({ title, icon: Icon, children, action, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="px-4">
      <div className="flex items-center gap-1">
        <CollapsibleTrigger
          className="flex min-w-0 flex-1 items-center gap-2 py-3.5 text-left text-md font-medium text-fg"
          data-testid={testId}
        >
          <ChevronRight
            className={cn(
              "size-4 shrink-0 text-fg-3 transition-transform",
              open && "rotate-90"
            )}
          />
          <Icon className="size-4 shrink-0 text-fg-3" />
          {title}
        </CollapsibleTrigger>
        {action}
      </div>
      <CollapsibleContent className="flex flex-col gap-3 pb-4 pl-6">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

type Props = {
  onSearch: (query: string) => void;
  onOpen: (hit: LibraryHit) => void;
  activeId: string | null;
};

/**
 * Before a search: the attorney's latest searches, what just changed in the
 * library, and searches suggested by the visa types the firm works on.
 */
export const ResearchLanding: FC<Props> = ({ onSearch, onOpen, activeId }) => {
  const updates = useLibrarySearch("updates", "", { limit: 6 });
  const visas = useFirmVisaCounts();
  // Read on mount: this view is only shown when no search is open, so a
  // search made since reappears here.
  const [recent, setRecent] = useState<string[]>(readRecentSearches);
  const suggestions = suggestionsFor(visas.data ?? []);

  return (
    <div className={RESEARCH_COLUMN} data-testid="research-landing">
      <Surface className="divide-y divide-line-1">
        <Section
          title="My Latest Searches"
          icon={Clock}
          testId="research-section-recent"
          action={
            recent.length ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-fg-3"
                onClick={() => {
                  clearRecentSearches();
                  setRecent([]);
                }}
                data-testid="research-recent-clear"
              >
                Clear
              </Button>
            ) : null
          }
        >
          {recent.length ? (
            <div
              className="flex flex-col gap-0.5"
              data-testid="research-recent"
            >
              {recent.map((query) => (
                <SearchRow key={query} query={query} onSearch={onSearch} />
              ))}
            </div>
          ) : (
            <p
              className="text-sm text-fg-3"
              data-testid="research-recent-empty"
            >
              Searches you run in the library show up here.
            </p>
          )}
        </Section>

        <Section
          title="Latest Policy Updates"
          icon={BellRing}
          testId="research-section-updates"
        >
          {updates.isPending ? (
            <Skeleton className="h-32 w-full rounded-xl" />
          ) : (
            <ul className="-mx-3 divide-y divide-line-1">
              {(updates.data?.results ?? []).map((hit, i) => (
                <li key={hit.id ?? i}>
                  <button
                    type="button"
                    disabled={!hit.id}
                    onClick={() => onOpen(hit)}
                    className={cn(
                      "flex w-full flex-col gap-0.5 rounded-md px-3 py-2.5 text-left hover:bg-fill-1",
                      hit.id && hit.id === activeId && "bg-fill-3"
                    )}
                  >
                    <span className="truncate text-base text-fg">
                      {hit.title}
                    </span>
                    <MetaLine parts={[hit.citation, hit.date]} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section
          title="Suggestions"
          icon={Sparkles}
          testId="research-section-suggestions"
        >
          {visas.isPending ? (
            <Skeleton className="h-24 w-full rounded-xl" />
          ) : suggestions.length ? (
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              {suggestions.map((s) => (
                <div key={s.visa} className="flex flex-col gap-1">
                  <span className="text-sm text-fg-3">
                    Based on your {s.label} cases
                  </span>
                  {s.queries.map((query) => (
                    <SearchRow key={query} query={query} onSearch={onSearch} />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <p
              className="text-sm text-fg-3"
              data-testid="research-suggestions-empty"
            >
              Suggestions appear here once your firm has cases.
            </p>
          )}
        </Section>
      </Surface>
    </div>
  );
};
